/** Формат «Аналитики Ozon»: рубли без копеек, неразрывные пробелы, «−» вместо дефиса,
 *  проценты с 1 знаком (по модулю < 1% — с 2 знаками). */

export const NBSP = "\u00a0";
export const MINUS = "−";

/** Целое по модулю с разрядами через неразрывный пробел: 1234567 → «1 234 567». */
function groupDigits(n: number): string {
  return String(Math.abs(Math.round(n))).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Знак числа после округления до целого (чтобы −0,4 не превращалось в «−0»). */
function signOfRounded(n: number): -1 | 0 | 1 {
  const r = Math.round(n);
  return r > 0 ? 1 : r < 0 ? -1 : 0;
}

/** Целое: «1 234», «−56». */
export function int(n: number): string {
  return (signOfRounded(n) < 0 ? MINUS : "") + groupDigits(n);
}

/** Целое со знаком для дельт: «+12», «−5», «0». */
export function intSigned(n: number): string {
  const s = signOfRounded(n);
  return (s > 0 ? "+" : s < 0 ? MINUS : "") + groupDigits(n);
}

/** Рубли без копеек: «1 234 ₽», «−56 ₽». */
export function rub(n: number): string {
  return `${int(n)}${NBSP}₽`;
}

/** Рубли со знаком для дельт: «+1 234 ₽», «−56 ₽», «0 ₽». */
export function rubSigned(n: number): string {
  return `${intSigned(n)}${NBSP}₽`;
}

/** Рубли с копейками (цена клика): «19,69 ₽», «1 234,50 ₽»; `signed` — со знаком для дельт. */
export function rubKop(n: number, signed = false): string {
  const cents = Math.round(n * 100);
  const [i, f] = (Math.abs(cents) / 100).toFixed(2).split(".");
  const sign = cents < 0 ? MINUS : signed && cents > 0 ? "+" : "";
  return `${sign}${i!.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)},${f}${NBSP}₽`;
}

/** Число процентов (уже ×100) → «12,3» / «0,45»; по модулю < 1 — 2 знака. */
function pctDigits(p: number): string {
  // сначала срезаем шум плавающей точки: (0,75 − 0,7375) × 100 = 1,2499999… → 1,25 → «1,3»
  const abs = Math.abs(Math.round(p * 1e9) / 1e9);
  const digits = abs < 1 ? 2 : 1;
  return abs.toFixed(digits).replace(".", ",");
}

/** Доля (0.123) → «12,3%», «−0,45%»; null → «—». Ноль → «0%». */
export function pct(frac: number | null): string {
  if (frac === null || !Number.isFinite(frac)) return "—";
  const p = frac * 100;
  if (p === 0) return "0%";
  const text = pctDigits(p);
  const isZero = /^0,0+$/.test(text);
  return `${p < 0 && !isZero ? MINUS : ""}${text}%`;
}

/** Доля со знаком для дельт: «+12,3%», «−4,0%». */
export function pctSigned(frac: number | null): string {
  if (frac === null || !Number.isFinite(frac)) return "—";
  const p = frac * 100;
  if (p === 0) return "0%";
  const text = pctDigits(p);
  if (/^0,0+$/.test(text)) return `${text}%`;
  return `${p > 0 ? "+" : MINUS}${text}%`;
}

/** Разница долей (доли, не проценты) в процентных пунктах: 0.012 → «+1,2 п.п.». */
export function pp(diff: number | null): string {
  if (diff === null || !Number.isFinite(diff)) return "—";
  const p = diff * 100;
  if (p === 0) return `0${NBSP}п.п.`;
  const text = pctDigits(p);
  if (/^0,0+$/.test(text)) return `${text}${NBSP}п.п.`;
  return `${p > 0 ? "+" : MINUS}${text}${NBSP}п.п.`;
}

/** Разница сверки: копейки важны (сверка «до копейки») — с двумя знаками и знаком; ноль — «0». */
export function diffText(v: number): string {
  if (Math.abs(v) < 0.005) return "0";
  const s = Math.abs(v).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${v < 0 ? MINUS : "+"}${s}${NBSP}₽`;
}

/** Дата дд.мм.гггг из YYYY-MM-DD (без часовых поясов). */
export function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
}

/** «дд.мм.гггг чч:мм МСК» из ISO-метки; бэкенд отдаёт её уже в МСК (+03:00), пояс не пересчитываем. */
export function fmtDateTimeMsk(s: string | null | undefined): string {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]} МСК` : s;
}

const MONTH_SHORT = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const MONTH_IN = [
  "январе", "феврале", "марте", "апреле", "мае", "июне",
  "июле", "августе", "сентябре", "октябре", "ноябре", "декабре",
];

export const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

/** «Октябрь 2026» из "2026-10". */
export function monthTitle(month: string): string {
  return `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
}

/** «19.10» из "2026-10-19". */
export function dayMonth(s: string): string {
  return `${s.slice(8, 10)}.${s.slice(5, 7)}`;
}

/** «сен 2026» из "2026-09" или "2026-09-01". */
export function monthLabel(month: string): string {
  return `${MONTH_SHORT[Number(month.slice(5, 7)) - 1]}${NBSP}${month.slice(0, 4)}`;
}

/** «в сентябре 2026» из "2026-09". */
export function monthIn(month: string): string {
  return `в ${MONTH_IN[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
}

/** Корзина тренда: месяц — «сен 2026», неделя/день — «07.09». */
export function bucketLabel(bucket: string, grain: "day" | "week" | "month"): string {
  if (grain === "month") return monthLabel(bucket);
  return `${bucket.slice(8, 10)}.${bucket.slice(5, 7)}`;
}

/** Дата или дата-время источника: «15.10.2026» / «15.10.2026 06:30» (время — местное);
 *  null — «нет данных»; не дата — как есть. */
export function fmtWhen(s: string | null): string {
  if (!s) return "нет данных";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return fmtDate(s);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const two = (n: number): string => String(n).padStart(2, "0");
  return `${two(d.getDate())}.${two(d.getMonth() + 1)}.${d.getFullYear()} ${two(d.getHours())}:${two(d.getMinutes())}`;
}

/** Относительное время «N назад». */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const min = Math.round((now - new Date(iso).getTime()) / 60000);
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  return `${Math.round(h / 24)} дн назад`;
}

/**
 * Час (МСК), с которого отсутствие сегодняшней сборки витрины считается проблемой. Ежедневная
 * сборка стартует в 10:30 МСК и идёт около 45–55 минут, к 12:00 она обязана закончиться; до этого
 * часа вчерашняя сборка — штатное состояние, предупреждения нет.
 */
export const MART_REBUILD_DEADLINE_HOUR_MSK = 12;

const MSK_FMT = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Moscow",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

interface MskParts {
  day: number; // порядковый номер дня (для сравнения дат), UTC-полночь даты МСК, мс
  dd: string;
  mm: string;
  hh: string;
  mi: string;
  hour: number;
}

function mskParts(ms: number): MskParts {
  const parts: Record<string, string> = {};
  for (const x of MSK_FMT.formatToParts(new Date(ms))) parts[x.type] = x.value;
  const p = (k: string): string => {
    const v = parts[k];
    if (v === undefined) throw new Error(`Intl не вернул поле ${k}`);
    return v;
  };
  return {
    day: Date.UTC(Number(p("year")), Number(p("month")) - 1, Number(p("day"))),
    dd: p("day"),
    mm: p("month"),
    hh: p("hour"),
    mi: p("minute"),
    hour: Number(p("hour")),
  };
}

/** Метка бэкенда → мс. Без пояса (так приходит из SQLite) — UTC, как хранит DWH; иначе не угадываем. */
function parseTs(iso: string): number {
  const ms = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`).getTime();
  if (Number.isNaN(ms)) throw new Error(`Не разобрать метку времени: ${iso}`);
  return ms;
}

/** «ДД.ММ в ЧЧ:ММ МСК» — абсолютное время сборки витрины. */
export function fmtBuiltMsk(iso: string): string {
  const p = mskParts(parseTs(iso));
  return `${p.dd}.${p.mm} в ${p.hh}:${p.mi} МСК`;
}

/**
 * Предупреждение «витрина не пересобиралась»: сборка раньше сегодняшнего дня (МСК) и сейчас уже
 * не раньше MART_REBUILD_DEADLINE_HOUR_MSK, либо раньше вчерашнего дня (в любое время).
 * null — витрина свежая.
 */
export function martStaleWarning(builtAt: string, now: number = Date.now()): string | null {
  const b = mskParts(parseTs(builtAt));
  const n = mskParts(now);
  const DAY = 86_400_000;
  const behindToday = b.day < n.day && n.hour >= MART_REBUILD_DEADLINE_HOUR_MSK;
  const behindYesterday = b.day < n.day - DAY;
  if (!behindToday && !behindYesterday) return null;
  return `Витрина за сегодня не пересобиралась — последняя сборка ${b.dd}.${b.mm} в ${b.hh}:${b.mi}`;
}

/** Компактное число для осей: 1 200 000 → «1,2 млн», −45 300 → «−45 тыс». */
export function compact(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? MINUS : "";
  // 999 500 … 999 999 округляются до 1000 тыс — это уже «1 млн»
  if (abs >= 1e6 || Math.round(abs / 1e3) >= 1000) {
    const v = abs / 1e6;
    return `${sign}${v.toFixed(abs >= 1e7 ? 0 : 1).replace(".", ",").replace(/,0$/, "")}${NBSP}млн`;
  }
  if (abs >= 1e3) return `${sign}${Math.round(abs / 1e3)}${NBSP}тыс`;
  return int(n);
}
